import { useState } from 'react'

import { Button } from '@/components/ui'
import SelectField from '@/components/select-field'
import { Icon } from '@/common/components/Icon'
import {
  CONTINUOUS_QUEST_TYPES,
  ContinuousQuestSummary,
  QuestDraft,
  QuestGroupUpsertDto,
  QuestUpsertDto,
  RoadmapDto,
  TagDto,
  TargetDraft,
  TargetDraftKind,
  emptyTargetDraft,
  targetDraftError,
} from '@/common/models/gamification'

import { translationTitle } from '../shared/labels'
import { QuestEditorModal } from './QuestEditorModal'
import { QuestGroupEditorModal } from './QuestGroupEditorModal'

interface TargetAuthoringProps {
  value: TargetDraft
  onChange: (draft: TargetDraft) => void
  allRoadmaps: RoadmapDto[]
  currentRoadmapId: string
  /**
   * Active continuous quests across the whole festival edition, with their time windows — the scope
   * the server enforces. Mirrored here so a placement conflict surfaces before submit.
   */
  continuousQuests?: ContinuousQuestSummary[]
  /** Festival edition id — threaded down to the quest editor's token-pool picker. */
  editionId: string
}

const KIND_OPTIONS: { value: TargetDraftKind; label: string }[] = [
  { value: 'NONE', label: 'No target' },
  { value: 'QUEST', label: 'Single Quest' },
  { value: 'QUEST_GROUP', label: 'Quest Group' },
  { value: 'ROADMAP', label: 'Roadmap' },
]

const ROADMAP_NONE = 'NONE'

const questLabel = (quest: QuestDraft): string => {
  const title = translationTitle(quest.translations, 'Untitled quest')
  const type = quest.config?.typeKey ?? ''
  return `${title} · ${type} · ${quest.points ?? 0} pts`
}

export const TargetAuthoring = ({
  value,
  onChange,
  allRoadmaps,
  currentRoadmapId,
  continuousQuests = [],
  editionId,
}: TargetAuthoringProps) => {
  // Nested-modal state
  const [questModalOpen, setQuestModalOpen] = useState(false)
  const [editingQuestIndex, setEditingQuestIndex] = useState<number | null>(
    null,
  )
  const [groupModalOpen, setGroupModalOpen] = useState(false)

  const error = targetDraftError(value)

  const setKind = (kind: TargetDraftKind) => onChange(emptyTargetDraft(kind))

  // ── Single quest ──────────────────────────────────────────────────────────
  const singleQuest = value.kind === 'QUEST' ? value.quest : null

  const openSingleQuest = () => {
    setEditingQuestIndex(null)
    setQuestModalOpen(true)
  }

  // ── Group quests ──────────────────────────────────────────────────────────
  const groupQuests = value.kind === 'QUEST_GROUP' ? value.group.quests : []

  const openAddGroupQuest = () => {
    setEditingQuestIndex(null)
    setQuestModalOpen(true)
  }

  const openEditGroupQuest = (index: number) => {
    setEditingQuestIndex(index)
    setQuestModalOpen(true)
  }

  const removeGroupQuest = (index: number) => {
    if (value.kind !== 'QUEST_GROUP') return
    const quest = value.group.quests[index]
    const quests = value.group.quests.filter((_, i) => i !== index)
    const removedQuestIds = quest.id
      ? [...value.group.removedQuestIds, quest.id]
      : value.group.removedQuestIds
    onChange({
      kind: 'QUEST_GROUP',
      group: { ...value.group, quests, removedQuestIds },
    })
  }

  // The quest passed to the (shared) quest modal, depending on context.
  const modalQuest: QuestDraft | null =
    value.kind === 'QUEST'
      ? singleQuest
      : editingQuestIndex !== null
      ? groupQuests[editingQuestIndex] ?? null
      : null

  const handleQuestSubmit = (data: QuestUpsertDto, tags: TagDto[]) => {
    if (value.kind === 'QUEST') {
      onChange({
        kind: 'QUEST',
        // Editing a quest's config doesn't change its id or QR deep link (both server-minted, tied
        // to the persisted quest), so carry the previously-loaded deepLink forward rather than
        // dropping it — QuestUpsertDto never carries it.
        quest: {
          ...data,
          id: value.quest?.id,
          deepLink: value.quest?.deepLink,
          tags,
        },
      })
      return
    }
    if (value.kind === 'QUEST_GROUP') {
      if (editingQuestIndex !== null) {
        const existing = value.group.quests[editingQuestIndex]
        const quests = value.group.quests.map((q, i) =>
          i === editingQuestIndex
            ? { ...data, id: existing?.id, deepLink: existing?.deepLink, tags }
            : q,
        )
        onChange({ kind: 'QUEST_GROUP', group: { ...value.group, quests } })
      } else {
        onChange({
          kind: 'QUEST_GROUP',
          group: {
            ...value.group,
            quests: [...value.group.quests, { ...data, tags }],
          },
        })
      }
    }
  }

  const handleGroupMetaSubmit = (meta: QuestGroupUpsertDto, tags: TagDto[]) => {
    if (value.kind !== 'QUEST_GROUP') return
    onChange({ kind: 'QUEST_GROUP', group: { ...value.group, meta, tags } })
  }

  // Continuous siblings inside this same (unsaved) group draft count too: the server list only
  // reflects persisted quests, so an in-progress sibling would otherwise be invisible. Projected
  // into the same shape so the overlap check treats both sources identically.
  const groupDraftContinuous: ContinuousQuestSummary[] = groupQuests
    .filter(
      (q, i) =>
        i !== editingQuestIndex &&
        q.active &&
        CONTINUOUS_QUEST_TYPES.includes(q.config?.typeKey),
    )
    .map((q) => ({
      id: q.id ?? '',
      typeKey: q.config.typeKey,
      timeWindowStart: q.timeWindowStart,
      timeWindowEnd: q.timeWindowEnd,
      roadmapId: currentRoadmapId,
      roadmapTitle: 'this quest group',
      title: translationTitle(q.translations, 'Untitled quest'),
    }))
  const questContinuousQuests = [...continuousQuests, ...groupDraftContinuous]

  return (
    <div className="space-y-4">
      <SelectField
        label="Target"
        value={value.kind}
        onValueChange={(v) => setKind(v as TargetDraftKind)}
        options={KIND_OPTIONS}
      />

      {/* Single quest */}
      {value.kind === 'QUEST' && (
        <div className="rounded-lg border border-monochrome-120 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <span className="text-body-medium text-monochrome-1">
              {singleQuest
                ? questLabel(singleQuest)
                : 'No quest configured yet.'}
            </span>
            <Button
              variant="primary-bordered"
              onClick={openSingleQuest}
              type="button"
            >
              {singleQuest ? 'Edit quest' : 'Configure quest'}
            </Button>
          </div>
        </div>
      )}

      {/* Quest group */}
      {value.kind === 'QUEST_GROUP' && (
        <div className="space-y-4 rounded-lg border border-monochrome-120 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-monochrome-2 text-sm">
              {value.group.meta.availabilityType} · complete{' '}
              {value.group.meta.completeCount} of {groupQuests.length}
            </div>
            <Button
              variant="secondary"
              onClick={() => setGroupModalOpen(true)}
              type="button"
            >
              Group settings
            </Button>
          </div>

          <div className="space-y-2">
            {groupQuests.length === 0 && (
              <div className="text-sm text-monochrome-2">No quests yet.</div>
            )}
            {groupQuests.map((quest, index) => (
              <div
                key={quest.id ?? `new-${index}`}
                className="flex items-center justify-between gap-3 rounded-md bg-monochrome-4-60 px-3 py-2"
              >
                <span className="text-body-medium text-monochrome-1">
                  {questLabel(quest)}
                </span>
                <div className="flex items-center gap-4">
                  <a
                    className="text-accent-1 underline"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      openEditGroupQuest(index)
                    }}
                  >
                    Edit
                  </a>
                  <a
                    className="text-accent-3 underline"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      removeGroupQuest(index)
                    }}
                  >
                    Remove
                  </a>
                </div>
              </div>
            ))}
          </div>

          <Button
            variant="primary-bordered"
            onClick={openAddGroupQuest}
            type="button"
          >
            Add Quest
            <Icon
              icon="plus-circle"
              width="18"
              height="18"
              viewBox="0 0 18 18"
            />
          </Button>
        </div>
      )}

      {/* Roadmap */}
      {value.kind === 'ROADMAP' && (
        <SelectField
          label="Roadmap"
          placeholder="Select a roadmap"
          value={value.roadmapId ?? ROADMAP_NONE}
          onValueChange={(v) =>
            onChange({
              kind: 'ROADMAP',
              roadmapId: v === ROADMAP_NONE ? null : v,
            })
          }
          options={[
            { value: ROADMAP_NONE, label: 'Select a roadmap' },
            ...allRoadmaps
              .filter((r) => r.id !== currentRoadmapId)
              .map((r) => ({
                value: r.id,
                label: translationTitle(r.translations, `${r.scope} roadmap`),
              })),
          ]}
        />
      )}

      {error && value.kind !== 'NONE' && (
        <span className="text-xs text-accent-3">{error}</span>
      )}

      {/* Shared quest editor (single quest + group members) */}
      <QuestEditorModal
        open={questModalOpen}
        onOpenChange={setQuestModalOpen}
        quest={modalQuest}
        onSubmit={handleQuestSubmit}
        continuousQuests={questContinuousQuests}
        editionId={editionId}
      />

      {/* Group settings editor */}
      {value.kind === 'QUEST_GROUP' && (
        <QuestGroupEditorModal
          open={groupModalOpen}
          onOpenChange={setGroupModalOpen}
          group={value.group.meta}
          tags={value.group.tags}
          editionId={editionId}
          onSubmit={handleGroupMetaSubmit}
        />
      )}
    </div>
  )
}
